import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Users, Lightbulb, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { mockData } from '../mock';

const About = () => {
  const valueIcons = {
    'Inovação constante': Lightbulb,
    'Foco no cliente': Users,
    'Autonomia e flexibilidade': Target,
    'Resultados mensuráveis': Shield
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-purple-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Sobre o {mockData.company.name}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Transformando relacionamentos em oportunidades de negócio
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Nossa Missão
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                {mockData.about.mission}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Acreditamos que cada interação com o cliente é uma oportunidade de construir relacionamentos duradouros e gerar valor para o negócio.
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl p-12 h-96 flex items-center justify-center">
              <div className="text-center">
                <Target className="h-32 w-32 text-blue-600 mx-auto mb-4" />
                <p className="text-gray-700 font-medium text-lg">Foco em Resultados</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl p-12 h-96 flex items-center justify-center">
                <div className="text-center">
                  <Lightbulb className="h-32 w-32 text-purple-600 mx-auto mb-4" />
                  <p className="text-gray-700 font-medium text-lg">Inovação Contínua</p>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Nossa Visão
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                {mockData.about.vision}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Queremos ser reconhecidos como a plataforma que melhor entende e atende as necessidades de automação de relacionamento das empresas modernas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Nossos Valores
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Princípios que guiam nossa forma de trabalhar e entregar valor
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockData.about.values.map((value, index) => {
              const Icon = valueIcons[value];
              return (
                <Card key={index} className="text-center border-2 hover:border-blue-600 hover:shadow-lg transition-all duration-300 group">
                  <CardHeader>
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="h-8 w-8 text-blue-600" />
                    </div>
                    <CardTitle className="text-lg">{value}</CardTitle>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Por que escolher a Manubot?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Uma plataforma desenhada para escalar seus resultados
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-xl">Tecnologia de Ponta</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Utilizamos as melhores práticas e tecnologias do mercado para garantir performance, segurança e escalabilidade.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-xl">Suporte Especializado</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Nossa equipe está sempre pronta para ajudar você a extrair o máximo valor da plataforma.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-xl">Evolução Constante</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Estamos sempre evoluindo a plataforma com base no feedback dos clientes e tendências do mercado.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Vamos conversar sobre seu projeto?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Entre em contato e descubra como podemos ajudar sua empresa a crescer
          </p>
          <Link to="/contato">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
              Entrar em Contato
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;